/**
 * Register Page — account creation. Registration does not sign the user in;
 * the backend expects a separate login, so we redirect to /login on success.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Check, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { registerSchema, type RegisterFormData } from '@/schemas/auth';
import { useAuthStore } from '@/stores/auth-store';
import { getErrorMessage } from '@/config/api';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { Alert, Button, Field, Input } from '@/components/ui';
import { cn } from '@/lib/cn';

export default function RegisterPage() {
  const navigate = useNavigate();
  const registerUser = useAuthStore((s) => s.register);
  const isLoading = useAuthStore((s) => s.isLoading);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const password = watch('password', '');

  // Mirrors registerSchema — keep the two in sync.
  const passwordChecks = [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'One uppercase letter', valid: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', valid: /[a-z]/.test(password) },
    { label: 'One digit', valid: /[0-9]/.test(password) },
  ];

  const onSubmit = async (data: RegisterFormData) => {
    setError('');
    try {
      await registerUser(data.email, data.username, data.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  if (success) {
    return (
      <AuthLayout title="Account created" description="Taking you to the sign-in page…">
        <div
          role="status"
          className="flex flex-col items-center gap-3 rounded-xl border border-success/25 bg-success/8 px-6 py-8 text-center"
        >
          <CheckCircle2 className="h-8 w-8 text-success" aria-hidden="true" />
          <p className="text-sm text-fg-muted">
            Your account is ready. Sign in to connect a broker and start trading.
          </p>
          <Button variant="secondary" size="sm" onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create account"
      description="Set up your trading workspace."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-fg transition-colors hover:text-fg">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" id="register-form" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Email" error={errors.email?.message}>
          {(field) => (
            <Input {...field} type="email" autoComplete="email" placeholder="you@example.com" {...register('email')} />
          )}
        </Field>

        <Field label="Username" error={errors.username?.message} hint="Letters, numbers, underscores and hyphens.">
          {(field) => (
            <Input {...field} type="text" autoComplete="username" placeholder="john_trader" {...register('username')} />
          )}
        </Field>

        <Field label="Password" error={errors.password?.message}>
          {(field) => (
            <Input
              {...field}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="rounded p-1.5 text-fg-faint transition-colors hover:text-fg-muted"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              }
              {...register('password')}
            />
          )}
        </Field>

        {password.length > 0 && (
          <ul className="-mt-1 flex flex-col gap-1" aria-label="Password requirements">
            {passwordChecks.map(({ label, valid }) => (
              <li key={label} className="flex items-center gap-2 text-xs">
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 items-center justify-center rounded-full border',
                    valid ? 'border-success/40 bg-success/15' : 'border-line-strong'
                  )}
                >
                  {valid && <Check className="h-2.5 w-2.5 text-success" aria-hidden="true" />}
                </span>
                <span className={valid ? 'text-success' : 'text-fg-subtle'}>{label}</span>
                <span className="sr-only">{valid ? '— met' : '— not met'}</span>
              </li>
            ))}
          </ul>
        )}

        <Field label="Confirm password" error={errors.confirmPassword?.message}>
          {(field) => (
            <Input
              {...field}
              type="password"
              autoComplete="new-password"
              placeholder="Re-enter your password"
              {...register('confirmPassword')}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          id="register-submit"
          className="mt-1"
        >
          {isLoading ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
